import React, {useContext, useEffect, useRef} from 'react';
import {BryntumGrid} from "@bryntum/schedulerpro-react";
import {GRANTS} from "../../other/constants/PrivilegeTypes.jsx";
import useRefsInit from "../hooks/useRefsInit.jsx";
import BryntumContext from "../context/bryntumContext.jsx";
import {TASK_BUDGET_GRID_CONFIG} from "../../other/constants/TaskBudgetGridConfig.jsx";
import useEnableViewTracking from "../hooks/useEnableViewTracking.jsx";
import { VIEW_TYPES } from "../../other/constants/OtherKeys.jsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import { EventHelper } from "@bryntum/schedulerpro";
import { callOnLoadOnColumns, debounce } from "../../other/utils/otherUtils.jsx";
import { processCellMenuItems } from "../../other/utils/gridCellMenuUtils.jsx";
import { PERSISTENCE_STATE_KEYS } from '../../other/constants/PersistenceStateKeys.jsx';
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";

const TaskBudgetPage = () => {
  const gridRef = useRef(null);
  const gridContainerRef = useRef(null);
  const gridFooterListener = useRef(null);
  const {setRefs, isRefsInit} = useRefsInit([gridRef]);

  const { project, user, editorHandler, userPreferences, entityManager } = useContext(BryntumContext);

  useEnableViewTracking(gridRef, VIEW_TYPES.taskBudget, isRefsInit);

  useEffect(() => {
    const budgetGrid = gridRef.current?.instance;
    if (project && budgetGrid && user && user.can(GRANTS.grant_two)) {
      budgetGrid.store = project.taskBudgetStore;
      callOnLoadOnColumns(budgetGrid.columns, project, { user })
      budgetGrid.features.cellMenu.processItems = processCellMenuItems({
        grid: budgetGrid,
        promptOnRemove: true
      })
      if (!gridFooterListener.current) {
        gridFooterListener.current = EventHelper.on({
          element: budgetGrid.footerContainer,
          click: ({ target }) => {
            if (target.id === 'swivu-budget-summary-checkbox') {
              budgetGrid.features.summary.selectedOnly = target.checked;
            }
          }
        })
        return () => {
          gridFooterListener.current();
          gridFooterListener.current = null;
        }
      }
    }
  }, [project, gridRef, user]);


  const toggleBudgetFocusMode = (isBudgetFocusMode) => {
    /* Remove main-grid-view class from grid container if focus mode is enabled, The main-grid-view css class enables all the custom theme styling which we want to be disabled when focus mode is enabled */
    gridContainerRef.current.classList.toggle("main-grid-view", !isBudgetFocusMode);
  }

  useEffect(() => {
    if (userPreferences) {
      /* Initialize set the focus mode state on the grid */
      toggleBudgetFocusMode(userPreferences.getSetting(PERSISTENCE_STATE_KEYS.taskBudgetFocusMode) === String(true))

      /* Listen to the user preferences changes and update the focus mode state on the grid */
      return userPreferences.on("change", ({ record }) => {
        if (record?.key === PERSISTENCE_STATE_KEYS.taskBudgetFocusMode) toggleBudgetFocusMode(record.value === String(true))
      })
    }
  }, [userPreferences]);

  const onOpenEditorByEntityType = debounce(({ event, column, record }) => {
    if(record.isGroupHeader) return;

    if (column.id === 'budget-tasks') {
      if (event.detail === 1) {
        editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
          entityRecord: record,
          event,
          column
        }, { by: "recordType", useExisting: true })
      } else {
        const { id } = event.target.dataset;
        if (id) {
          const task = project.extraTaskStore.getById(id);
          entityManager.openEntityManager({entityType: ENTITY_TYPES.TASK, entityId: task.id});
        }
      }
    } else if (column.id === "task-budget-column") {
      editorHandler.openEditor(EDITOR_KEYS.budgetEditor, record, {
        budget: record
      }, { by: "recordType", useExisting: true })
    }
  }, 300)

  const onClickHeaderMenuItem = ({ item }) => {
    if (item.ref === 'addNewEntity') {
      const newRecord = project.taskBudgetStore.createRecord();
      editorHandler.openEditor(EDITOR_KEYS.budgetEditor, newRecord, { budget: newRecord });
    }
  }

  return (
    <div ref={gridContainerRef} className="flex flex-1">
      <BryntumGrid
        ref={setRefs(gridRef)}
        {...TASK_BUDGET_GRID_CONFIG}
        flex={1}
        disabled={user && !user.can(GRANTS.grant_two)}
        readOnly={user && !user.can(GRANTS.grant_two)}
        onCellClick={onOpenEditorByEntityType}
        preserveScrollOnDatasetChange
        onHeaderMenuItem={onClickHeaderMenuItem}
        searchFeature={true}
      />
    </div>
  );
};

export default TaskBudgetPage;
